// ==========================
// Version 43 — App.js
// - Adds "/verify-email"
// - Blocks protected app routes until email/password users verify their email
// - Adds "/auth/action" custom Firebase email action handler
// - Keeps public routes visible while Firebase auth performs its first check
// - Allows localhost-only mock admin auth to pass protected layout checks
// - Adds localhost-only mock route jump for static preview servers without SPA fallback
// ==========================
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom';

import Navbar from './components/Navbar';
import MemoryForm from './MemoryForm';
import Register from './auth/Register';
import Login from './auth/Login';
import VerifyEmail from './auth/VerifyEmail';
import AuthActionHandler from './auth/AuthActionHandler';
import { needsEmailVerification } from './auth/emailVerification';
import MemoryDetails from './pages/MemoryDetails';
import { useAuth } from './context/AuthContext';
import PublicMap from './pages/PublicMap';

import EditMemoryModal from './components/EditMemoryModal';
import ConfirmDialog from './components/ConfirmDialog';
import BrandLogo from './components/BrandLogo';

import {
  updateMemoryDoc,
  deleteMemoryAndFiles,
} from './db';

// Home deps (logged-in app home)
import Hero from './components/Hero';
import MapAndStrip from './components/MapAndStrip';
import TimelineFeed from './components/TimelineFeed';
import PolaroidGrid from './components/PolaroidGrid';
import { isMemoryMarkedMinted } from './nft/status';

// SEO + layout
import Seo from './components/Seo';
import Footer from './components/Footer';

// New: toasts, 404, boundary
import ToastHub from './components/ToastHub';
import NotFound from './pages/NotFound';
import AccessDenied from './pages/AccessDenied';
import AdminGate from './auth/AdminGate';
import ErrorBoundary from './components/ErrorBoundary';

// Static pages
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

// NEW: Profile
import Profile from './pages/Profile';

// NEW: NFT test page
import NFTTestPage from './pages/NFTTestPage';

// NEW: Admin
import { db } from './firebase';
import {
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
} from 'firebase/firestore';

import AdminDashboard from './pages/AdminDashboard';
import AdminLayout from './pages/AdminLayout';
import AdminUsers from './pages/AdminUsers';
import AdminMemories from './pages/AdminMemories';
import AdminActivity from './pages/AdminActivity';
import AdminFlags from './pages/AdminFlags';
import AdminModeration from './pages/AdminModeration';

// NEW: Landing Page
import LandingPage from './pages/LandingPage';
import LandingPage3 from './pages/LandingPage3';

// Brand palette (single definition)
const NAVY = '#0A1A33';
const IVORY = '#FDFCF9';
const GOLD = '#C9A44C';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';

// Scroll to top on route change
function ScrollToTop() {
  const location = useLocation();
  React.useEffect(() => {
    try {
      window.history.scrollRestoration = 'manual';
    } catch {}
  }, []);
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);
  return null;
}

// Route-change announcer for screen readers
function RouteChangeAnnouncer() {
  const { pathname } = useLocation();
  const [msg, setMsg] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => {
      const title = document.title || 'Page loaded';
      setMsg(title);
    }, 0);
    return () => clearTimeout(t);
  }, [pathname]);
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clipPath: 'inset(50%)',
      }}
    >
      {msg}
    </div>
  );
}

// Dev-only console banner (one-time)
function DevConsoleHint() {
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.info(
      '%cBlessed with a Kiss — dev mode',
      'background:#C9A44C;color:#0A1A33;padding:2px 6px;border-radius:6px;font-weight:900',
      '\nTips:',
      '\n• Routes auto-focus <main> on navigation.',
      '\n• Check <ErrorBoundary> resets per route.',
      '\n• To debug SEO tags, inspect <head> (data-seo).'
    );
  }, []);
  return null;
}

// diag helper
const logged = new Set();
function isValidComponent(mod, name) {
  const ok = typeof mod === 'function';
  if (!ok && !logged.has(name)) {
    console.error(
      `[BWAK diagnostics] "${name}" is not a React component. typeof =`,
      typeof mod,
      'value =',
      mod
    );
    logged.add(name);
  }
  return ok;
}

/* RequireAuth: redirects to /login if not signed in */
function RequireAuth({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) {
    return (
      <div
        style={{
          minHeight: '40vh',
          display: 'grid',
          placeItems: 'center',
          color: IVORY,
          background: NAVY,
        }}
      >
        Checking sign-in…
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" replace />;
  if (needsEmailVerification(currentUser)) return <Navigate to="/verify-email" replace />;
  return children;
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();

  // Focus <main> on route change
  const mainRef = React.useRef(null);
  React.useEffect(() => {
    const t = setTimeout(() => mainRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [location.key]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    if (!isLocalHost) return;

    const params = new URLSearchParams(location.search);
    const mockRoute = params.get('bwak_mock_route');
    if (!mockRoute?.startsWith('/') || mockRoute.startsWith('//')) return;

    navigate(mockRoute, { replace: true });
  }, [location.search, navigate]);

  return (
    <>
      <ScrollToTop />
      <RouteChangeAnnouncer />
      <DevConsoleHint />
      <a href="#main" style={skipLinkStyle}>
        Skip to content
      </a>
      <Navbar />
      <ToastHub />

      <ErrorBoundary key={location.key} onReset={() => window.scrollTo(0, 0)}>
        <main id="main" role="main" tabIndex={-1} ref={mainRef}>
          <Routes>
            {/* Public Landing */}
            <Route path="/" element={<LandingPage3 />} />
            <Route
              path="/landingpage-3"
              element={<LandingPage pagePath="/landingpage-3" />}
            />

            {/* Logged-in app home */}
            <Route
              path="/home"
              element={
                <RequireAuth>
                  <Home />
                </RequireAuth>
              }
            />

            <Route path="/map" element={<PublicMap />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/action" element={<AuthActionHandler />} />
            <Route path="/m/:id" element={<MemoryDetails />} />

            {/* Static pages */}
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/403" element={<AccessDenied />} />

            {/* Profile */}
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <Profile />
                </RequireAuth>
              }
            />

            {/* NFT test (internal) */}
            <Route
              path="/nft-test"
              element={
                <RequireAuth>
                  <NFTTestPage />
                </RequireAuth>
              }
            />

            {/* Admin (protected) with nested sections) */}
            <Route
              path="/admin"
              element={
                <AdminGate>
                  <AdminLayout />
                </AdminGate>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="memories" element={<AdminMemories />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="flags" element={<AdminFlags />} />
              <Route path="moderation" element={<AdminModeration />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </ErrorBoundary>

      <Footer />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

/* -------------------------------
   HOME (logged-in app home)
-------------------------------- */
function Home() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [memories, setMemories] = React.useState([]);
  const [viewMode, setViewMode] = React.useState('timeline'); // or 'polaroid'

  // edit modal
  const [editing, setEditing] = React.useState({ open: false, memory: null });

  // confirm delete
  const [confirm, setConfirm] = React.useState({
    open: false,
    memory: null,
    busy: false,
  });

  // ✅ LIVE memories subscription
  React.useEffect(() => {
    if (!currentUser?.uid) {
      setMemories([]);
      setLoading(false);
      return () => {};
    }

    setLoading(true);

    const qy = query(
      collection(db, 'memories'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          firestoreId: d.id,
          ...d.data(),
        }));
        setMemories(list);
        setLoading(false);
      },
      (err) => {
        console.error('[BWAK] Home memories snapshot failed:', err);
        alert('Failed to load your memories.');
        setLoading(false);
      }
    );

    return () => unsub();
  }, [currentUser?.uid]);

  const handleAddMemory = (memory) => setMemories((prev) => [memory, ...prev]);

  const openEdit = (memory) => {
    if (isMemoryMarkedMinted(memory)) {
      alert('Minted memories cannot be edited.');
      return;
    }
    setEditing({ open: true, memory });
  };
  const closeEdit = () => setEditing({ open: false, memory: null });

  const saveEdit = async (updates) => {
    const mem = editing.memory;
    const targetId = mem?.firestoreId;
    if (!targetId) {
      closeEdit();
      return;
    }
    if (isMemoryMarkedMinted(mem)) {
      alert('Minted memories cannot be edited.');
      closeEdit();
      return;
    }
    try {
      await updateMemoryDoc(targetId, updates);
    } catch (err) {
      console.error('Failed to update Firestore:', err);
      alert(
        err?.code === 'permission-denied'
          ? 'Update not allowed by server rules.'
          : 'Failed to save changes.'
      );
      return;
    }
    closeEdit();
  };

  const requestDelete = (memory) => {
    if (isMemoryMarkedMinted(memory)) {
      alert('Minted memories cannot be deleted.');
      return;
    }
    setConfirm({ open: true, memory, busy: false });
  };
  const cancelDelete = () =>
    setConfirm({ open: false, memory: null, busy: false });

  const confirmDelete = async () => {
    const mem = confirm.memory;
    if (!mem) return;
    if (isMemoryMarkedMinted(mem)) {
      alert('Minted memories cannot be deleted.');
      cancelDelete();
      return;
    }
    setConfirm((c) => ({ ...c, busy: true }));
    const targetId = mem.firestoreId;

    // Optimistic UI: remove immediately; snapshot will re-sync if deletion fails
    setMemories((prev) => prev.filter((m) => m.firestoreId !== targetId));

    try {
      if (targetId) await deleteMemoryAndFiles(targetId);
    } catch (err) {
      console.error('Delete failed:', err);
      alert(
        err?.code === 'permission-denied'
          ? 'Delete not allowed by server rules.'
          : 'Failed to delete from server.'
      );
    } finally {
      cancelDelete();
    }
  };

  const showHero = isValidComponent(Hero, 'Hero');
  const showMapAndStrip =
    isValidComponent(MapAndStrip, 'MapAndStrip') && !!currentUser;
  const showTimeline = isValidComponent(TimelineFeed, 'TimelineFeed');
  const showPolaroid = isValidComponent(PolaroidGrid, 'PolaroidGrid');

  const startMemoryScroll = () => {
    const el = document.getElementById('memory-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={styles.page}>
      <Seo
        title="Home • Blessed With A Kiss"
        description="Save your memories with photos, stories and locations. Explore your life on a map."
        type="website"
      />

      {showHero ? <Hero /> : <DiagBox name="Hero" />}

      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          padding: '0 12px 16px',
        }}
      >
        {showMapAndStrip ? (
          <section style={styles.section} aria-busy={loading ? 'true' : 'false'}>
            <h2 style={styles.h2}>Your Map</h2>
            {loading ? (
              <div
                role="status"
                aria-label="Loading your map and recent memories"
                style={{ display: 'grid', gap: 10 }}
              >
                <SkeletonBlock height={420} />
                <SkeletonRow />
              </div>
            ) : (
              <MapAndStrip userId={currentUser.uid} memories={memories} />
            )}
          </section>
        ) : (
          <section style={styles.section}>
            <h2 style={styles.h2}>Explore the Map</h2>
            <div style={styles.notice}>
              {currentUser
                ? 'Map section unavailable (component import issue).'
                : 'Sign in to see your personal pins. Public map is available from the top nav.'}
            </div>
          </section>
        )}
      </div>

      <div
        id="memory-form"
        style={{ maxWidth: 900, margin: '0 auto', padding: '0 12px' }}
      >
        <section style={styles.section}>
          <h2 style={styles.h2}>Start a Memory</h2>
          <MemoryForm onAddMemory={handleAddMemory} />
        </section>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 12px' }}>
        <section style={styles.section} aria-busy={loading ? 'true' : 'false'}>
          <div style={styles.headerRow}>
            <h2 style={styles.h2}>Your Memories</h2>
            <div style={styles.viewToggle} role="tablist" aria-label="View mode">
              <button
                role="tab"
                aria-selected={viewMode === 'timeline'}
                onClick={() => setViewMode('timeline')}
                style={{
                  ...styles.toggleBtn,
                  ...(viewMode === 'timeline' ? styles.toggleActive : {}),
                }}
              >
                Timeline
              </button>
              <button
                role="tab"
                aria-selected={viewMode === 'polaroid'}
                onClick={() => setViewMode('polaroid')}
                style={{
                  ...styles.toggleBtn,
                  ...(viewMode === 'polaroid' ? styles.toggleActive : {}),
                }}
              >
                Polaroid
              </button>
            </div>
          </div>

          {loading && (
            <div
              role="status"
              aria-label="Loading your memories"
              style={{ display: 'grid', gap: 10, marginTop: 6 }}
            >
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {!loading &&
            memories.length > 0 &&
            (viewMode === 'timeline'
              ? showTimeline ? (
                  <TimelineFeed
                    memories={memories}
                    onEdit={openEdit}
                    onDelete={requestDelete}
                  />
                ) : (
                  <DiagBox name="TimelineFeed" />
                )
              : showPolaroid ? (
                  <PolaroidGrid memories={memories} />
                ) : (
                  <DiagBox name="PolaroidGrid" />
                ))}

          {!loading && memories.length === 0 && (
            <div style={emptyStyles.card}>
              <BrandLogo size={52} decorative style={emptyStyles.logo} />
              <h3 style={emptyStyles.title}>Your story starts here</h3>
              <p style={emptyStyles.sub}>
                Drop a pin, add a date, and write a short note. We’ll keep it safe.
              </p>
              <button type="button" onClick={startMemoryScroll} style={emptyStyles.cta}>
                Start a Memory
              </button>
            </div>
          )}
        </section>
      </div>

      <EditMemoryModal
        open={editing.open}
        memory={editing.memory}
        onClose={closeEdit}
        onSave={saveEdit}
      />
      <ConfirmDialog
        open={confirm.open}
        title="Delete this memory?"
        message="This will remove the memory and any photos it contains."
        confirmText="Delete"
        danger
        loading={confirm.busy}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

function DiagBox({ name }) {
  return (
    <div style={styles.diag}>
      <strong>{name}</strong> failed to render (not a component). Check its file export.
    </div>
  );
}

/* --- skeletons (unchanged) --- */
function SkeletonBlock({ height = 140 }) {
  return (
    <div
      style={{
        height,
        borderRadius: 12,
        background: `linear-gradient(90deg,
          rgba(253,252,249,0.06) 25%,
          rgba(253,252,249,0.13) 37%,
          rgba(253,252,249,0.06) 63%)`,
        backgroundSize: '400% 100%',
        animation: 'bwak-shimmer 1.2s ease-in-out infinite',
        border: '1px solid rgba(201,164,76,0.20)',
      }}
    />
  );
}

function SkeletonRow() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
      <SkeletonBlock height={84} />
      <SkeletonBlock height={84} />
      <SkeletonBlock height={84} />
    </div>
  );
}

function SkeletonCard() {
  return (
    <div
      style={{
        background: IVORY,
        color: NAVY,
        borderRadius: 16,
        border: '1px solid rgba(201,164,76,0.35)',
        padding: 12,
        boxShadow: '0 8px 28px rgba(10,26,51,0.25)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <SkeletonBlock height={16} />
      </div>
      <SkeletonBlock height={180} />
      <div style={{ marginTop: 8 }}>
        <SkeletonBlock height={14} />
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: 'calc(100dvh - 64px)',
    background: NAVY,
    position: 'relative',
    paddingBottom: 36,
  },
  section: { marginTop: 18 },
  h2: {
    color: IVORY,
    fontFamily: 'serif',
    fontSize: 22,
    margin: '0 0 8px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  viewToggle: {
    display: 'inline-flex',
    background: 'rgba(253,252,249,0.06)',
    borderRadius: 999,
    padding: 4,
    border: '1px solid rgba(201,164,76,0.25)',
  },
  toggleBtn: {
    appearance: 'none',
    background: 'transparent',
    color: IVORY,
    border: 'none',
    padding: '6px 12px',
    borderRadius: 999,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 13,
  },
  toggleActive: { background: GOLD, color: NAVY },
  notice: {
    color: WARM_GRAY,
    padding: 12,
    background: 'rgba(253,252,249,0.06)',
    borderRadius: 12,
    border: '1px solid rgba(201,164,76,0.20)',
  },
  diag: {
    color: BURGUNDY,
    background: 'rgba(92,26,43,0.08)',
    border: '1px dashed rgba(183,110,121,0.55)',
    padding: 10,
    borderRadius: 10,
    margin: '8px 0',
  },
};

const emptyStyles = {
  card: {
    marginTop: 8,
    background: IVORY,
    color: NAVY,
    borderRadius: 16,
    border: '1px solid rgba(201,164,76,0.35)',
    boxShadow: '0 8px 28px rgba(10,26,51,0.25)',
    padding: 16,
    textAlign: 'center',
  },
  logo: {
    width: 52,
    height: 52,
    borderRadius: 14,
    border: '1px solid rgba(10,26,51,0.12)',
    display: 'inline-flex',
    margin: '0 auto 8px',
    padding: 5,
    background: 'rgba(10,26,51,0.04)',
  },
  title: { margin: '6px 0 4px', fontFamily: 'serif' },
  sub: { margin: '0 0 10px', color: WARM_GRAY },
  cta: {
    background: GOLD,
    color: NAVY,
    border: 'none',
    padding: '10px 14px',
    borderRadius: 12,
    fontWeight: 900,
    cursor: 'pointer',
  },
};

const skipLinkStyle = {
  position: 'absolute',
  left: '-999px',
  top: 'auto',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  zIndex: 10000,
  background: GOLD,
  color: NAVY,
  padding: '8px 10px',
  borderRadius: 8,
};

/* keyframes for shimmer (inline so no CSS dep) */
(() => {
  if (typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.textContent = `
    @keyframes bwak-shimmer {
      0% { background-position: 0% 0; }
      100% { background-position: 100% 0; }
    }
  `;
  document.head.appendChild(el);
})();

export default App;
// ==========================
// End Version 36 — App.js
// ==========================
